import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { SpeciesData } from '../../data-models/catch-table-models/species-data.model';
import { DailyCatchReport } from '../../data-models/report-models/daily-catch-report.model';

@Component({
    selector: 'app-offload-table-dialog',
    templateUrl: './offload-table-dialog.component.html',
    styleUrls: ['./offload-table-dialog.component.css'],
    standalone: false
})
export class OffloadTableDialogComponent implements OnInit {

  // programming flags
  selectReports: boolean = false; // does the user want to select from specific catch reports
  dateSelection: boolean = false; // does the the user want to select by date
  listSelection: boolean = false; // does the user want to select by a list
  includePieces: boolean = false; // does the user want to include pieces 

  // form control for catch report list selector
  catchReportList: FormControl = new FormControl<DailyCatchReport[]>([]);

  // data from offload report
  catchReportData: DailyCatchReport[] = [];
  tableData: SpeciesData[] = [];

  // general form controls
  offloadForm: UntypedFormGroup = new UntypedFormGroup({
    dataType: new FormControl<string>('', [Validators.required]),
    piecesIncluded: new FormControl<string>('', [Validators.required]),
    selectionType: new FormControl<string>(''),
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null)
  });

  constructor(
    private dialogRef: MatDialogRef<OffloadTableDialogComponent>,
    public sharedMethodsService: SharedMethodsService,
    @Inject(MAT_DIALOG_DATA) private data: { catchReports: DailyCatchReport[], tableData: SpeciesData[] }
  ) {

    this.catchReportData = this.data.catchReports;
    this.tableData = this.data.tableData;

    this.offloadForm.patchValue({ // fill include pieces question
      piecesIncluded: 'Yes',
    });

  }


  /*  ---------- Initialization Methods  ---------- */


  ngOnInit(): void {

    this.onDataTypeChange();
    this.onSelectionTypeChange();

  }

  // listens for a change in question 1 form answer and updates programming flags and conditional validation of the next question
  onDataTypeChange(): void {

    this.offloadForm.get('dataType')?.valueChanges.subscribe((value: string) => {
      const selectionTypeControl: AbstractControl<any, any> | null = this.offloadForm.get('selectionType');

      if (value === 'Specific catch reports from this trip') {
        this.selectReports = true;
        selectionTypeControl?.setValidators([Validators.required]); // enable validation for selection type question
      } else if (value === 'All catch reports from this trip') { // if they want to include all catch reports
        this.selectReports = false;
        selectionTypeControl?.clearValidators(); // disable validation for selection type question
      }
      selectionTypeControl?.updateValueAndValidity();
    });

  }

  // listens for a change in question 2 form answer and updates programming flags
  onSelectionTypeChange(): void {

    this.offloadForm.get('selectionType')?.valueChanges.subscribe((value: string) => {

      if (value === 'Select by date') {
        this.dateSelection = true;
        this.listSelection = false; // reset other flag
      } else if (value === 'Select from list') { //if they want to select from a list
        this.listSelection = true;
        this.dateSelection = false; // reset other flag
      }
    });

  }

    
  /*  ---------- Helper Methods ---------- */


  // makes a list of all species in the provided catchData parameter who's kept field is greater than 0
  makeCatchList(catchData: SpeciesData[]): SpeciesData[] {

    let refinedList: SpeciesData[] = [];

    for (let species of catchData) {
      if (species.kept > 0) {
        refinedList.push(species);
      }
    }

    return refinedList;

  }

  // determines if the date fished for the current catch report is within the user-selected date range
  evaluateDateInfo(catchReport: DailyCatchReport): boolean {

    let startDate: Date = new Date(this.offloadForm.get('startDate')?.value);
    let endDate: Date = new Date(this.offloadForm.get('endDate')?.value);
    let dateFished: Date = this.clearTime(new Date(catchReport.dateFished));

    return ((dateFished.getTime() >= startDate.getTime()) && (dateFished.getTime() <= endDate.getTime()));

  }

  // determines if the current catch report is present in the user-selected list of catch reports
  evaluateSelectedInfo(catchReport: DailyCatchReport): boolean { 

    let catchReports: DailyCatchReport[] = this.catchReportList.value;
    for (let report of catchReports) {
      // POSSIBLY CHANGE TO COMPARE CONFIRMATION NUMBERS IN THE FUTURE
      let date1: Date = new Date(report.completionDate);
      let date2: Date = new Date(catchReport.completionDate);
      if(date1.getTime() === date2.getTime()){
        return true;
      }
    }
    return false;

  }

  // removes any time data from a Date object (used for date comparisons)
  clearTime(currDate: Date): Date {

    currDate.setHours(0);
    currDate.setMinutes(0);
    currDate.setSeconds(0);
    currDate.setMilliseconds(0);
    return currDate;

  }


  /*  ---------- Dialog Close Methods ---------- */


  // invoked if the user selects the "Apply" button
  populateTable(): void {

    this.tableData.length = 0; // clear any previous table data

    if (this.offloadForm.get('piecesIncluded')?.value === 'Yes'){
      this.includePieces = true;
    }

    if (this.offloadForm.valid) {
      this.getTableData(this.selectReports, this.dateSelection, this.listSelection, this.includePieces); // fill table data source
      this.dialogRef.close(this.tableData); // pass new table data source back to the offload-report
    } else {
      this.offloadForm.markAllAsTouched();
    }
   
  }

   // invoked if the user selects the "Back" button
   closeDialog(): void {

    this.dialogRef.close();

  }


  /*  ---------- Table Data Methods ---------- */


  // retrieves table data based on the how the user has filled out the table population form
  getTableData(select: boolean, date: boolean, list: boolean, pieces: boolean): void {

    let speciesNamesList: string[] = []; // temp storage for species names (used for comparison)

    // loop through each daily catch report in currTrip, and add all species data to the table source
    for (let catchReport of this.catchReportData) { // loop through each catch report
      let isIncluded: boolean = true;
      if (select) {
        if (date) {
          isIncluded = this.evaluateDateInfo(catchReport);
        } else if (list) { // they will have selected from the list
          isIncluded = this.evaluateSelectedInfo(catchReport);
        } 
      }
      if (isIncluded) {
        this.addSpeciesToData(catchReport, speciesNamesList, pieces);
      }
    }

  }

  // adds a species to the table data source if the appropriate conditions are met
  addSpeciesToData(catchReport: DailyCatchReport, speciesNamesList: string[], pieces: boolean): void {

    for (let species of catchReport.speciesDataList) { // loop through each species list of the catch report
      // only include if the species is not already in our list and it was kept 
      if (species.kept > 0) { 
        if (!speciesNamesList.includes(species.speciesName)){ // if we do not already have the species in our list
          let tempSpecies: SpeciesData = new SpeciesData(species.speciesName); // create new species object 
          tempSpecies.speciesCode = species.speciesCode;
          tempSpecies.sizeCode = species.sizeCode;
          if (pieces) {
          tempSpecies.offloadPieces = species.kept; // update the number of kept pieces
          }
          this.tableData.push(tempSpecies); // add species to the table
          speciesNamesList.push(species.speciesName); // keep track of the species name in our list
        // if we already have the species in our table data source list but need to update the pieces
        } else if (pieces) { 
          for (let currSpecies of this.tableData){
            if (currSpecies.speciesName === species.speciesName) {
              currSpecies.offloadPieces += species.kept; // update the number of kept pieces
            }
          }
        }

      }
    }

  }

}
